/**
 * Globe geometry utilities.
 *
 * Creates Three.js BufferGeometry with dual positions (sphere + flat) for
 * GPU-based morphing between curved and flat states.
 *
 * All geometry uses the concave globe coordinate system:
 * - Camera at origin looking forward (-Z direction)
 * - lat=0, lng=0 maps to [0, 0, -radius] (center of view)
 * - Latitude rotates up/down (Y axis), longitude rotates left/right (X axis)
 */

import * as THREE from 'three'
import { GLOBE_CONFIG } from '../constants'
import type { GeoBounds } from '@/domain/geo/types'

// Re-export for convenience (canonical source is @/domain/geo/types)
export type { GeoBounds } from '@/domain/geo/types'

/**
 * Calculate sphere and flat positions for a given lat/lng coordinate.
 *
 * @param lat - Latitude in degrees
 * @param lng - Longitude in degrees
 * @param radius - Sphere radius
 * @returns Object with sphere and flat position arrays [x, y, z]
 */
function calculatePositions(
  lat: number,
  lng: number,
  radius: number
): { sphere: [number, number, number]; flat: [number, number, number] } {
  const latRad = (lat * Math.PI) / 180
  const lngRad = (lng * Math.PI) / 180

  // Spherical position (concave globe coordinate system)
  const sphereX = radius * Math.cos(latRad) * Math.sin(lngRad)
  const sphereY = radius * Math.sin(latRad)
  const sphereZ = -radius * Math.cos(latRad) * Math.cos(lngRad)

  // Flat position (plane at z=-radius)
  const flatX = (lngRad / Math.PI) * radius
  const flatY = (latRad / (Math.PI / 2)) * radius
  const flatZ = -radius

  return {
    sphere: [sphereX, sphereY, sphereZ],
    flat: [flatX, flatY, flatZ],
  }
}

/**
 * Generate triangle indices for a grid of vertices.
 *
 * @param widthSegments - Number of segments along width
 * @param heightSegments - Number of segments along height
 * @returns Array of triangle indices (winding for interior visibility)
 */
function generateGridIndices(widthSegments: number, heightSegments: number): number[] {
  const indices: number[] = []

  for (let row = 0; row < heightSegments; row++) {
    for (let col = 0; col < widthSegments; col++) {
      const a = row * (widthSegments + 1) + col
      const b = a + widthSegments + 1
      const c = a + 1
      const d = b + 1

      // Two triangles per quad (winding for interior visibility)
      indices.push(a, c, b)
      indices.push(c, d, b)
    }
  }

  return indices
}

/**
 * Options for UV coordinate generation.
 */
interface UVOptions {
  /** Whether to flip V coordinate (default: true for standard texture mapping) */
  flipV?: boolean
}

/**
 * Creates geometry for the full globe hemisphere.
 *
 * Covers the entire visible globe from MAX_LATITUDE to MIN_LATITUDE
 * and -180° to +180° longitude. Used by MorphingHemisphere and CountryGlobe.
 *
 * @param radius - Sphere radius in world units
 * @param widthSegments - Number of horizontal segments
 * @param heightSegments - Number of vertical segments
 * @param uvOptions - Options for UV coordinate generation
 * @returns BufferGeometry with position, flatPosition, and uv attributes
 *
 * @example
 * ```tsx
 * const geometry = useMemo(
 *   () => createHemisphereGeometry(radius, segments, segments / 2),
 *   [radius, segments]
 * )
 * ```
 */
export function createHemisphereGeometry(
  radius: number,
  widthSegments: number,
  heightSegments: number,
  uvOptions: UVOptions = {}
): THREE.BufferGeometry {
  const { flipV = true } = uvOptions

  const spherePositions: number[] = []
  const flatPositions: number[] = []
  const uvs: number[] = []

  // Geographic bounds (Web Mercator limits)
  const maxLat = GLOBE_CONFIG.MAX_LATITUDE
  const minLat = GLOBE_CONFIG.MIN_LATITUDE
  const minLng = -180
  const maxLng = 180

  // Generate vertices using geographic lat/lng grid
  for (let row = 0; row <= heightSegments; row++) {
    for (let col = 0; col <= widthSegments; col++) {
      const u = col / widthSegments
      const v = row / heightSegments

      // Geographic coordinates
      const lat = maxLat - v * (maxLat - minLat)
      const lng = minLng + u * (maxLng - minLng)

      // Calculate both positions
      const { sphere, flat } = calculatePositions(lat, lng, radius)
      spherePositions.push(...sphere)
      flatPositions.push(...flat)

      // UV for texture mapping
      uvs.push(u, flipV ? 1 - v : v)
    }
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setIndex(generateGridIndices(widthSegments, heightSegments))
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(spherePositions, 3))
  geometry.setAttribute('flatPosition', new THREE.Float32BufferAttribute(flatPositions, 3))
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))

  return geometry
}

/**
 * Creates geometry for a geographic tile or bounded region.
 *
 * Used by TileMesh for rendering map tiles at specific bounds.
 *
 * @param bounds - Geographic bounds (north, south, east, west in degrees)
 * @param radius - Sphere radius in world units
 * @param subdivisions - Number of subdivisions (both width and height)
 * @returns BufferGeometry with position, flatPosition, and uv attributes
 *
 * @example
 * ```tsx
 * const geometry = useMemo(
 *   () => createTileGeometry(tile.bounds, radius, 4),
 *   [tile.bounds, radius]
 * )
 * ```
 */
export function createTileGeometry(
  bounds: GeoBounds,
  radius: number,
  subdivisions: number = 4
): THREE.BufferGeometry {
  const spherePositions: number[] = []
  const flatPositions: number[] = []
  const uvs: number[] = []

  // Create a grid of vertices to follow the curved surface
  for (let y = 0; y <= subdivisions; y++) {
    for (let x = 0; x <= subdivisions; x++) {
      const u = x / subdivisions
      const v = y / subdivisions

      // Interpolate lat/lng within tile bounds
      const lat = bounds.north - v * (bounds.north - bounds.south)
      const lng = bounds.west + u * (bounds.east - bounds.west)

      // Calculate both positions
      const { sphere, flat } = calculatePositions(lat, lng, radius)
      spherePositions.push(...sphere)
      flatPositions.push(...flat)

      // UV coordinates for texture
      uvs.push(u, 1 - v)
    }
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setIndex(generateGridIndices(subdivisions, subdivisions))
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(spherePositions, 3))
  geometry.setAttribute('flatPosition', new THREE.Float32BufferAttribute(flatPositions, 3))
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))

  return geometry
}

/**
 * Calculate corner positions for debug overlay (boundary lines).
 *
 * Returns morphed positions (interpolated between sphere and flat) for
 * the four corners and center of a geographic bounds.
 *
 * @param bounds - Geographic bounds
 * @param curvature - Current curvature value (0 = flat, 1 = sphere)
 * @param radius - Sphere radius
 * @returns Object with corner and center positions as THREE.Vector3
 */
export function calculateCornerPositions(
  bounds: GeoBounds,
  curvature: number,
  radius: number
): {
  nw: THREE.Vector3
  ne: THREE.Vector3
  sw: THREE.Vector3
  se: THREE.Vector3
  center: THREE.Vector3
} {
  function latLngToMorphed(lat: number, lng: number): THREE.Vector3 {
    const { sphere, flat } = calculatePositions(lat, lng, radius)

    const x = sphere[0] * curvature + flat[0] * (1 - curvature)
    const y = sphere[1] * curvature + flat[1] * (1 - curvature)
    const z = sphere[2] * curvature + flat[2] * (1 - curvature)

    return new THREE.Vector3(x, y, z)
  }

  const nw = latLngToMorphed(bounds.north, bounds.west)
  const ne = latLngToMorphed(bounds.north, bounds.east)
  const sw = latLngToMorphed(bounds.south, bounds.west)
  const se = latLngToMorphed(bounds.south, bounds.east)

  const centerLat = (bounds.north + bounds.south) / 2
  const centerLng = (bounds.west + bounds.east) / 2
  const center = latLngToMorphed(centerLat, centerLng)

  return { nw, ne, sw, se, center }
}
